import { KikUserZodSchemas as ZodSchemas } from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import {
  AddressesAddressDto,
  AddressesCountrySubdivisionDto,
  GetAddressRequirementsResponse,
  GetCountrySubdivisionsResponse,
} from "@springtree/eva-services-core";
import { get } from "lodash-es";
import { useTranslations } from "next-intl";
import { AriaAttributes, useEffect, useMemo, useRef, useState } from "react";
import { Controller, GlobalError, useFormContext } from "react-hook-form";

import ControlledPhoneInput from "~/components/form/controlled-phone-input";
import { useFormTranslations } from "~/hooks/use-form-translations";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import Logger from "~/services/logger/logger";
import formUtils from "~/utils/form-utils";
import textUtils from "~/utils/text-utils";

import LocalitiesAutocomplete, { LocalitySelectionSource } from "../localities-autocomplete/localities-autocomplete";
import ControlledCombobox from "./controlled-combobox";
import styles from "./dynamic-address-fields.module.scss";
import InputField, { OptionalCheckboxProps } from "./input-field";

// fixed address fields
export const addressMapSeparated = ["FirstName", "LastName", "EmailAddress", "PhoneNumber"];

type Props = {
  requirements: GetAddressRequirementsResponse["AddressRequirements"];
  subdivisions?: GetCountrySubdivisionsResponse["CountrySubdivisions"];
  fieldPrefix: string;
  onAutocompleteSelection: (newAddress: any) => void;
  watchedAddress: string;
};

export default function DynamicAddressFields({
  requirements,
  subdivisions,
  fieldPrefix,
  onAutocompleteSelection,
  watchedAddress,
}: Props) {
  const { register, control, formState, setValue, watch, clearErrors } = useFormContext();
  const t = useTranslations();
  const { getFieldError } = useFormTranslations();
  const { newWarningGlobalNotification } = useGlobalNotification();
  const houseNumberEmptyValue = formUtils.getHouseNumberEmptyValue();

  const address1 = watch(`${fieldPrefix}.Address1`);

  /**
   * This is needed because Woosmap captures the closure for `handleSelection`.
   * We update this ref's value each time we receive a new value through the props.
   */
  const subdivisionsRef = useRef<AddressesCountrySubdivisionDto[] | undefined>();

  useEffect(() => {
    subdivisionsRef.current = subdivisions;
  }, [subdivisions]);

  const hasDefaultValues = useMemo(
    () =>
      Object.keys(formState.defaultValues?.address).filter(
        (addressItem) => addressItem !== "CountryID" && formState.defaultValues?.address[addressItem] != undefined
      ).length > 0 || false,
    [formState.defaultValues?.address]
  );

  // if there is at least 1 field valorized the complete form should be visible
  const [isFormVisible, setIsFormVisible] = useState(hasDefaultValues);

  // if watchedAddress is valorized show the rest of the form - in order to show on autocomple
  useEffect(() => {
    if (typeof watchedAddress !== "undefined" && watchedAddress != "") {
      setIsFormVisible(true);
    } else {
      setIsFormVisible(false);
    }
  }, [watchedAddress]);

  // watch for errors in the hidden part of the form
  useEffect(() => {
    if (
      Object.keys((formState.errors?.address || {}) as any).filter((key) => !addressMapSeparated.includes(key)).length >
      0
    ) {
      setIsFormVisible(true);
    }
  }, [formState]);

  const handleSelection = (
    selection: Omit<AddressesAddressDto, "ID" | "CountryID">,
    source: LocalitySelectionSource = "suggestion"
  ) => {
    if (selection && requirements) {
      setIsFormVisible(true);

      const data = { ...selection };
      const houseNumberMaxLength = requirements.HouseNumber?.MaxLength;
      let houseNumberMovedToAddress2 = false;

      // Some providers return a building name/number that does not fit in the HouseNumber field (e.g. UK "Unit 17-18"):
      // move it to Address2 and flag the address as having no house number
      if (data.HouseNumber && houseNumberMaxLength && data.HouseNumber.length > houseNumberMaxLength) {
        data.Address2 = [data.Address2, data.HouseNumber].filter(Boolean).join(", ");
        data.HouseNumber = houseNumberEmptyValue;
        houseNumberMovedToAddress2 = true;
      }

      const updatedAddressFields: any = {};
      const formKeys = Object.keys(requirements)
        .filter((key) => !addressMapSeparated.includes(key) && key != "CountryID")
        .map((key) => `${fieldPrefix}.${key}`);

      formKeys.forEach((actualKey) => {
        // Ugly solution. But there's no other way to prevent overwriting a readonly field (point 2 of https://bitbucket.org/kiko-milano/unified-commerce/pull-requests/1205#comment-495061239)
        const fieldRef: HTMLInputElement | null =
          document.querySelector(`input[name="${actualKey}"]`) ||
          document.querySelector(`textarea[name="${actualKey}"]`) ||
          document.querySelector(`select[name="${actualKey}"]`);
        if (!fieldRef?.readOnly) {
          updatedAddressFields[actualKey] = "";
        }
      });

      for (const [field, value] of Object.entries(data)) {
        if (field === "CountryID") continue;
        const fieldRef: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null =
          document.querySelector(`input[name="${fieldPrefix}.${field}"]`) ||
          document.querySelector(`textarea[name="${fieldPrefix}.${field}"]`) ||
          document.querySelector(`select[name="${fieldPrefix}.${field}"]`);
        const isInputOrTextareaAndReadonly =
          (fieldRef instanceof HTMLInputElement || fieldRef instanceof HTMLTextAreaElement) && fieldRef?.readOnly;
        const isSelectAndDisabled = fieldRef instanceof HTMLSelectElement && fieldRef?.disabled;

        // A picked suggestion carrying a real house number overrides the "no house number" state:
        // setting the value also unchecks the flag
        const isHouseNumberFromSuggestion =
          field === "HouseNumber" && source === "suggestion" && !!value && value !== houseNumberEmptyValue;

        // if the field is readonly or disabled, remove the key from updatedAddressFields
        if ((isInputOrTextareaAndReadonly || isSelectAndDisabled) && !isHouseNumberFromSuggestion) {
          delete updatedAddressFields[`${fieldPrefix}.${field}`];
          continue;
        }

        if (typeof value === "string" && requirements[field].PredefinedValues) {
          const match = textUtils.findClosestMatch(value, requirements[field].PredefinedValues!);

          if (match) {
            updatedAddressFields[`${fieldPrefix}.${field}`] = match;
          }

          continue;
        }

        updatedAddressFields[`${fieldPrefix}.${field}`] = value;
      }

      if (subdivisionsRef.current && "CountrySubdivisionID" in requirements) {
        // We assume that the subdivision will always be the state.
        if (data.State) {
          const options = subdivisionsRef.current.map((v) => ({ label: v.Name, value: v.ID }));
          const match = options.find((o) => o.value.endsWith(`-${data.State}`))?.value;

          if (match) {
            updatedAddressFields[`${fieldPrefix}.CountrySubdivisionID`] = match;
          }
        }
      }

      onAutocompleteSelection(updatedAddressFields);

      // Address2 is filled by the autocomplete: clear it when the newly picked suggestion has no value for it
      const address2Key = `${fieldPrefix}.Address2`;
      if (
        source === "suggestion" &&
        "Address2" in requirements &&
        !data.Address2 &&
        address2Key in updatedAddressFields
      ) {
        setValue(address2Key, "", { shouldValidate: true });
      }

      if (houseNumberMovedToAddress2) {
        newWarningGlobalNotification({
          slug: "houseNumberMovedToAddress2",
          closeOnClickCta: true,
          ctaCaption: "generic.ok",
        });
      }
    }
  };

  const houseNumberValue = watch(`${fieldPrefix}.HouseNumber`);
  const hasEmptyHouseNumber = houseNumberValue === houseNumberEmptyValue;

  const fields = useMemo(() => {
    let inputs: JSX.Element[] = [];

    if (!requirements) {
      return inputs;
    }

    for (const [field, rules] of Object.entries(requirements)) {
      if (addressMapSeparated.includes(field)) {
        // skip separated fields
        continue;
      }
      const name = fieldPrefix ? `${fieldPrefix}.${field}` : field;
      const error = get(formState.errors, name.split(".")) as GlobalError | undefined;

      if (rules.Visible === 2) {
        // This field is hidden. Do not validate it.
        continue;
      }

      // if it has PredefinedValues use combobox component
      if (rules.PredefinedValues) {
        inputs.push(
          <ControlledCombobox
            name={name}
            key={name}
            control={control}
            label={t("generic.form." + field)}
            error={getFieldError(error?.message, {
              fieldName: t("generic.form." + field),
              maxLength: rules.MaxLength,
              minLength: rules.MinLength,
            })}
            isOptional={rules.Visible === 1}
            options={rules.PredefinedValues.map((v) => ({ label: v, value: v }))}
            tabIndex={isFormVisible ? undefined : -1}
          />
        );
        continue;
      }

      if (field === "CountrySubdivisionID") {
        if (!!subdivisions) {
          inputs.push(
            <ControlledCombobox
              name={name}
              key={name}
              control={control}
              label={t("generic.form." + field)}
              error={getFieldError(error?.message, {
                fieldName: t("generic.form." + field),
                maxLength: rules.MaxLength,
                minLength: rules.MinLength,
              })}
              isOptional={rules.Visible === 1}
              options={subdivisions.map((v) => ({ label: v.Name, value: v.ID }))}
              tabIndex={isFormVisible ? undefined : -1}
            />
          );
        }

        continue;
      }

      let fieldType: HTMLInputElement["type"] = "text";
      let id = undefined;
      let role = undefined;
      let ariaAutocomplete: AriaAttributes["aria-autocomplete"] = undefined;
      let optionalReadOnlyCheckbox: OptionalCheckboxProps | undefined = undefined;
      let placeholder: string | undefined = undefined;

      switch (field) {
        case "Address1":
          if (isFormVisible) {
            inputs.push(
              <LocalitiesAutocomplete
                key={name}
                name={name}
                label={t("generic.form." + field)}
                placeholder={t("generic.searchAddressPlaceholder")}
                onSelectLocality={handleSelection}
                error={getFieldError(error?.message, {
                  fieldName: t("generic.form." + field),
                  maxLength: rules.MaxLength,
                  minLength: rules.MinLength,
                })}
                isOptional={rules.Visible === 1}
                displayValue={address1}
              />
            );
          }
          continue;
        case "Address2":
          break;
        case "HouseNumber":
          // If the value that already comes from the address is equal to houseNumberEmptyValue (which should be "N/A"), already mark the field as readonly
          optionalReadOnlyCheckbox = {
            canBeConditionallyEmpty: true,
            defaultEmpty: hasEmptyHouseNumber,
            valueWhenEmpty: houseNumberEmptyValue,
            emptyCheckboxLabel: t("generic.noHouseNumber"),
          };
          break;
        case "District":
          break;
        case "City":
          break;
        case "CountryID":
          inputs.push(
            <Controller
              key={name}
              control={control}
              name={name}
              render={({ field: { value, ref } }) => (
                <InputField
                  ref={ref}
                  type={fieldType}
                  label={t("generic.form." + field)}
                  readOnly
                  disabled
                  value={value}
                />
              )}
            />
          );
          continue;
        case "ZipCode":
          placeholder = t("generic.form.ZipCode_placeholder");
          break;
        default:
          Logger.instance.warn(`Unexpected field ${field}.`);
          break;
      }

      inputs.push(
        <InputField
          type={fieldType}
          key={name}
          {...register(name)}
          label={t("generic.form." + field)}
          placeholder={placeholder}
          error={getFieldError(error?.message, {
            fieldName: t("generic.form." + field),
            maxLength: rules.MaxLength,
            minLength: rules.MinLength,
          })}
          isOptional={rules.Visible === 1}
          autoComplete="_off"
          tabIndex={isFormVisible ? undefined : -1}
          id={id}
          role={role}
          aria-autocomplete={ariaAutocomplete}
          clearErrors={name === "address.HouseNumber" ? clearErrors : undefined}
          setValue={name === "address.HouseNumber" ? setValue : undefined}
          optionalCheckbox={optionalReadOnlyCheckbox || undefined}
        />
      );
    }

    return inputs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirements, subdivisions, hasEmptyHouseNumber, isFormVisible, formState.errors, address1]);

  if (!requirements || Object.keys(requirements).length == 0) {
    return <></>;
  }

  return (
    <>
      {/* fixed part of the form (cross country) */}
      <InputField
        type={"text"}
        key={`${fieldPrefix}.FirstName`}
        {...register(`${fieldPrefix}.FirstName`)}
        label={t("generic.form.FirstName")}
        error={getFieldError((formState.errors?.address as any)?.FirstName?.message, {
          fieldName: t("generic.form.FirstName"),
          maxLength: requirements["FirstName"].MaxLength,
          minLength: requirements["FirstName"].MinLength,
        })}
        isOptional={requirements["FirstName"].Visible === 1}
        autoComplete="_off"
      />
      <InputField
        type={"text"}
        key={`${fieldPrefix}.LastName`}
        {...register(`${fieldPrefix}.LastName`)}
        label={t("generic.form.LastName")}
        error={getFieldError((formState.errors?.address as any)?.LastName?.message, {
          fieldName: t("generic.form.LastName"),
          maxLength: requirements["LastName"].MaxLength,
          minLength: requirements["LastName"].MinLength,
        })}
        isOptional={requirements["LastName"].Visible === 1}
        autoComplete="_off"
      />
      <InputField
        type={"email"}
        key={`${fieldPrefix}.EmailAddress`}
        {...register(`${fieldPrefix}.EmailAddress`)}
        label={t("generic.form.EmailAddress")}
        error={getFieldError((formState.errors?.address as any)?.EmailAddress?.message, {
          fieldName: t("generic.form.EmailAddress"),
          maxLength: requirements["EmailAddress"].MaxLength,
          minLength: requirements["EmailAddress"].MinLength,
        })}
        isOptional={requirements["EmailAddress"].Visible === 1}
        autoComplete="_off"
      />
      <ControlledPhoneInput
        key={`${fieldPrefix}.PhoneNumber`}
        control={control}
        name={`${fieldPrefix}.PhoneNumber`}
        label={t("generic.form.PhoneNumber")}
        isOptional={requirements["PhoneNumber"].Visible === 1}
        placeholder={t("generic.form.PhoneNumber_placeholder")}
        maxLength={ZodSchemas.phoneNumberMaxlength}
        error={getFieldError((formState.errors?.address as any)?.PhoneNumber?.message, {
          fieldName: t("generic.form.PhoneNumber"),
          maxLength: requirements["PhoneNumber"].MaxLength,
          minLength: requirements["PhoneNumber"].MinLength,
        })}
      />
      {/* autocomplete box */}
      {!isFormVisible && (
        <LocalitiesAutocomplete
          label={t("generic.searchAddress")}
          placeholder={t("generic.searchAddressPlaceholder")}
          onEditManually={() => setIsFormVisible(true)}
          isFormVisible={isFormVisible}
          onSelectLocality={handleSelection}
          showSearchIcon
          isOptional
        />
      )}
      {/* other dynamic fields */}
      <div
        className={styles.otherFields}
        style={{ height: !isFormVisible ? "0px" : "auto", overflow: isFormVisible ? "initial" : "hidden" }}
      >
        {fields}
      </div>
    </>
  );
}
